import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import {
  TrendingDown as CapitulationIcon,
  TrendingUp as SnapbackIcon,
  TrendingUp,
  Search as ScanIcon,
  Science as BacktestIcon,
  Visibility as WatchIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getReversalCatcherLocale } from '../components/reversal-catcher/reversalCatcherFormatters';
import { reversalCatcherAPI } from '../services/api';
import {
  CapitulationList,
  SnapbackList,
  SignalFilters,
  PerformanceMetrics,
  PerformanceMetricsSkeleton,
} from '../components/reversal-catcher';
import ReversalCatcherBacktest from './ReversalCatcherBacktest';

/** Tab order: actionable snapback first; capitulation = watchlist prep */
const RC_TAB = {
  SNAPBACK: 'snapback',
  CAPITULATION: 'capitulation',
  WATCHLIST: 'watchlist',
  BACKTEST: 'backtest',
};

const ReversalCatcherScanner = () => {
  const { t, i18n } = useTranslation();
  const dateLocale = getReversalCatcherLocale(i18n.language);
  // State
  const [refreshing, setRefreshing] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(RC_TAB.SNAPBACK);
  const [capitulationStocks, setCapitulationStocks] = useState([]);
  const [snapbackStocks, setSnapbackStocks] = useState([]);
  const [watchlistStocks, setWatchlistStocks] = useState([]);
  const [performance, setPerformance] = useState(null);
  const [filters, setFilters] = useState({
    snap_mode: 'conservative',
    min_cap_score: 2,
    limit: 20,
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info',
  });
  const [fetchError, setFetchError] = useState(null);
  const [latestScanDate, setLatestScanDate] = useState(null);
  const filtersMountedRef = useRef(false);

  // Fetch data
  const fetchData = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setRefreshing(true);
    setFetchError(null);
    try {
      let derivedLatestScanDate = null;
      const [capResult, snapResult, watchResult, perfResult] = await Promise.allSettled([
        reversalCatcherAPI.getCapitulation(filters),
        reversalCatcherAPI.getSnapback(filters),
        reversalCatcherAPI.getWatchlist({ ...filters, limit: 100 }),
        reversalCatcherAPI.getPerformance({ days: 30 }),
      ]);
      
      if (capResult.status === 'fulfilled') {
        const capData = capResult.value?.data ?? [];
        setCapitulationStocks(capData);
        // Derive latest scan date from signal_date in response data
        const dates = capData.map(s => s.signal_date).filter(Boolean);
        if (dates.length > 0) {
          derivedLatestScanDate = dates.sort().reverse()[0];
          setLatestScanDate(derivedLatestScanDate);
        }
      } else {
        console.error('Error fetching capitulation:', capResult.reason);
        setFetchError((prev) => prev || (capResult.reason?.message || capResult.reason?.response?.data?.detail || t('reversalCatcher.errors.capitulation')));
      }
      
      if (snapResult.status === 'fulfilled') {
        const snapData = snapResult.value?.data ?? [];
        setSnapbackStocks(snapData);
        if (!derivedLatestScanDate) {
          const dates = snapData.map(s => s.signal_date).filter(Boolean);
          if (dates.length > 0) {
            derivedLatestScanDate = dates.sort().reverse()[0];
            setLatestScanDate(derivedLatestScanDate);
          }
        }
      } else {
        console.error('Error fetching snapback:', snapResult.reason);
        setFetchError((prev) => prev || (snapResult.reason?.message || snapResult.reason?.response?.data?.detail || t('reversalCatcher.errors.snapback')));
      }
      
      if (watchResult.status === 'fulfilled') {
        setWatchlistStocks(watchResult.value?.data ?? []);
      } else {
        console.error('Error fetching watchlist:', watchResult.reason);
      }
      
      if (perfResult.status === 'fulfilled') {
        setPerformance(perfResult.value?.data ?? null);
      } else {
        console.error('Error fetching performance:', perfResult.reason);
        setFetchError((prev) => prev || (perfResult.reason?.message || perfResult.reason?.response?.data?.detail || t('reversalCatcher.errors.performance')));
      }
    } catch (error) {
      console.error('Error fetching reversal catcher data:', error);
      const errMsg = error?.message || t('reversalCatcher.errors.unknown');
      setFetchError(errMsg);
      setSnackbar({
        open: true,
        message: t('reversalCatcher.errors.loadData', { error: errMsg }),
        severity: 'error',
      });
    } finally {
      if (!silent) setRefreshing(false);
      setInitialLoading(false);
    }
  }, [filters, t]);
  
  // Auto-load on open + reload lists when filters change (GET only, not full scan)
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!filtersMountedRef.current) {
      filtersMountedRef.current = true;
      return;
    }
    setSnackbar({
      open: true,
      message: t('reversalCatcher.messages.filtersApplied'),
      severity: 'info',
    });
  }, [filters, t]);

  // Scan all stocks
  const handleScan = async () => {
    setScanning(true);
    setSnackbar({
      open: true,
      message: t('reversalCatcher.messages.scanStarted'),
      severity: 'info',
    });
    
    try {
      const response = await reversalCatcherAPI.scan({
        snap_mode: filters.snap_mode,
        save_to_db: true,
      });
      
      setSnackbar({
        open: true,
        message: t('reversalCatcher.messages.scanComplete', { count: response.data?.total_signals || 0 }),
        severity: 'success',
      });
      
      await fetchData({ silent: true });
    } catch (error) {
      console.error('Error scanning:', error);
      setSnackbar({
        open: true,
        message: t('reversalCatcher.messages.scanFailed', { error: error.response?.data?.detail || error.message || t('reversalCatcher.errors.unknown') }),
        severity: 'error',
      });
    } finally {
      setScanning(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  
  const showContentSkeleton = initialLoading || (refreshing && capitulationStocks.length === 0 && snapbackStocks.length === 0 && watchlistStocks.length === 0);

  const tabs = [
    { id: RC_TAB.SNAPBACK, icon: <SnapbackIcon sx={{ fontSize: 18 }} />, label: t('reversalCatcher.tabs.snapback'), count: snapbackStocks.length },
    { id: RC_TAB.CAPITULATION, icon: <CapitulationIcon sx={{ fontSize: 18 }} />, label: t('reversalCatcher.tabs.capitulation'), count: capitulationStocks.length },
    { id: RC_TAB.WATCHLIST, icon: <WatchIcon sx={{ fontSize: 18 }} />, label: t('reversalCatcher.tabs.watchlist'), count: watchlistStocks.length },
    { id: RC_TAB.BACKTEST, icon: <BacktestIcon sx={{ fontSize: 18 }} />, label: t('reversalCatcher.tabs.backtest'), count: null },
  ];

  return (
    <Container maxWidth="xl" sx={{ mt: 1.5, mb: 2.5, px: { xs: 1.5, sm: 2 } }}>
      {/* ═══════ HEADER ═══════ */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'flex-start', 
        justifyContent: 'space-between', 
        mb: 1.25,
        gap: 1,
        flexWrap: 'wrap',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: '10px',
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 3px 10px rgba(245, 87, 108, 0.25)',
          }}>
            <TrendingUp sx={{ color: 'white', fontSize: 18 }} />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
              <Typography sx={{ fontWeight: 800, color: '#0f172a', fontSize: '1.05rem', letterSpacing: '-0.01em', lineHeight: 1.2 }}>
                {t('reversalCatcher.title')}
              </Typography>
              {initialLoading ? (
                <Skeleton variant="rounded" width={88} height={22} sx={{ borderRadius: 1.5, bgcolor: '#f1f5f9' }} />
              ) : latestScanDate ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, bgcolor: '#f1f5f9', px: 1, py: 0.3, borderRadius: 1.5 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#f5576c', boxShadow: '0 0 6px #f5576c' }} />
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569' }}>
                    {new Date(latestScanDate).toLocaleDateString(dateLocale)}
                  </Typography>
                </Box>
              ) : null}
            </Box>
            <Typography sx={{ color: '#94a3b8', fontSize: '0.68rem', mt: 0.1, lineHeight: 1.35, maxWidth: 520 }}>
              {t('reversalCatcher.subtitle')}
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <SignalFilters filters={filters} onChange={setFilters} />
          <Button
            variant="outlined"
            size="small"
            startIcon={scanning ? <CircularProgress size={16} color="inherit" /> : <ScanIcon sx={{ fontSize: 16 }} />}
            onClick={handleScan}
            disabled={scanning || refreshing}
            sx={{
              borderRadius: '4px',
              borderColor: scanning ? '#667eea' : '#e2e8f0',
              color: scanning ? '#667eea' : '#64748b',
              fontWeight: 600,
              fontSize: 13,
              textTransform: 'none',
              '&:hover': {
                borderColor: '#667eea',
                color: '#667eea',
                backgroundColor: 'rgba(102,126,234,0.04)',
              },
              '& .MuiButton-startIcon': { mr: 0.5 },
            }}
          >
            {t('reversalCatcher.actions.scanAll')}
          </Button>
        </Box>
      </Box>
      
      {/* ═══════ ERROR ═══════ */}
      {fetchError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2, py: 0.5 }} onClose={() => setFetchError(null)}>
          <Typography sx={{ fontSize: '0.8rem' }}>
            {t('reversalCatcher.messages.loadError', { error: fetchError })}
          </Typography>
        </Alert>
      )}

      {/* ═══════ EMPTY STATE ═══════ */}
      {!showContentSkeleton && !fetchError && capitulationStocks.length === 0 && snapbackStocks.length === 0 && (!performance || performance.total_signals === 0) && (
        <Alert severity="info" icon={<ScanIcon />} sx={{ mb: 2, borderRadius: 2, py: 0.5 }}>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
            {t('reversalCatcher.messages.noData', { name: t('reversalCatcher.name') })}
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
            {latestScanDate
              ? t('reversalCatcher.messages.lastScanDate', { date: new Date(latestScanDate).toLocaleDateString(dateLocale) })
              : t('reversalCatcher.messages.noScanYet')}
            {' — '}{t('reversalCatcher.messages.runScan')}
          </Typography>
        </Alert>
      )}

      {/* ═══════ PERFORMANCE (compact) ═══════ */}
      {(showContentSkeleton || (performance && performance.total_signals > 0)) && (
        <Box sx={{ mb: 1 }}>
          {showContentSkeleton ? (
            <PerformanceMetricsSkeleton />
          ) : (
            <PerformanceMetrics data={performance} />
          )}
        </Box>
      )}
      
      {/* ═══════ TABS ═══════ */}
      <Box sx={{ 
        borderBottom: '2px solid #f1f5f9', 
        mb: 1.25, 
        mt: 0,
      }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, v) => setActiveTab(v)}
          sx={{ 
            minHeight: 36,
            '& .MuiTabs-indicator': {
              height: 2,
              borderRadius: '2px 2px 0 0',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            },
            '& .MuiTab-root': { 
              minHeight: 36, 
              fontSize: '0.76rem',
              fontWeight: 600,
              textTransform: 'none',
              color: '#94a3b8',
              px: 1.5,
              py: 0.5,
              '&.Mui-selected': { color: '#1e293b' },
            }
          }}
        >
          {tabs.map((tab) => (
            <Tab 
              key={tab.id}
              value={tab.id}
              icon={tab.icon} 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  {tab.label}
                  {tab.count !== null && (
                    <Box sx={{
                      bgcolor: activeTab === tab.id ? '#667eea' : '#e2e8f0',
                      color: activeTab === tab.id ? '#fff' : '#64748b',
                      fontSize: '0.6rem', fontWeight: 700,
                      borderRadius: 10, px: 0.75, py: 0.1,
                      minWidth: 18, textAlign: 'center', lineHeight: 1.4,
                    }}>
                      {tab.count}
                    </Box>
                  )}
                </Box>
              }
              iconPosition="start"
            />
          ))}
        </Tabs>
      </Box>
      
      {/* ═══════ CONTENT ═══════ */}
      <Box>
        {activeTab === RC_TAB.BACKTEST ? (
          initialLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} sx={{ color: '#667eea' }} />
            </Box>
          ) : (
            <ReversalCatcherBacktest />
          )
        ) : showContentSkeleton ? (
          activeTab === RC_TAB.SNAPBACK ? (
            <SnapbackList stocks={[]} onRefresh={fetchData} loading />
          ) : (
            <CapitulationList stocks={[]} onRefresh={fetchData} loading />
          )
        ) : (
          <>
            {activeTab === RC_TAB.SNAPBACK && (
              <SnapbackList 
                stocks={snapbackStocks}
                onRefresh={fetchData}
                loading={refreshing}
              />
            )}
            {activeTab === RC_TAB.CAPITULATION && (
              <CapitulationList 
                stocks={capitulationStocks}
                onRefresh={fetchData}
                loading={refreshing}
              />
            )}
            {activeTab === RC_TAB.WATCHLIST && (
              <CapitulationList 
                stocks={watchlistStocks}
                onRefresh={fetchData}
                loading={refreshing}
              />
            )}
          </>
        )}
      </Box>
      
      {/* ═══════ SNACKBAR ═══════ */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ReversalCatcherScanner;
